import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { getApiBase } from "@/lib/httpClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import BuscaCliente from "@/components/BuscaCliente";
import BuscaProcesso from "@/components/BuscaProcesso";

// Rótulo legível para a macro: `cliente.campos.numero_do_contrato` não é o que
// o advogado quer ler ao conferir se o documento vai sair certo.
const ROTULOS = {
  "cliente.nome": "Nome do cliente",
  "cliente.tipo": "Tipo de pessoa",
  "cliente.documento": "CPF/CNPJ",
  "cliente.documento_tipo": "Tipo do documento",
  "cliente.email": "E-mail do cliente",
  "cliente.telefone": "Telefone do cliente",
  "processo.numero_cnj": "Número do processo",
  "processo.classe": "Classe",
  "processo.assunto": "Assunto",
  "processo.orgao_julgador": "Órgão julgador",
  "processo.tribunal": "Tribunal",
  "processo.data_distribuicao": "Data de distribuição",
  "processo.valor_causa": "Valor da causa",
  "processo.descricao": "Descrição do processo",
  "processo.situacao": "Situação",
  "escritorio.nome": "Nome do escritório",
  "advogado.nome": "Seu nome",
  "advogado.email": "Seu e-mail",
  hoje: "Data de hoje",
  hoje_extenso: "Data por extenso",
};

export function rotuloDaMacro(caminho) {
  if (ROTULOS[caminho]) return ROTULOS[caminho];
  if (caminho.startsWith("cliente.campos.")) {
    return `Campo do cliente: ${humanizar(caminho.slice("cliente.campos.".length))}`;
  }
  return humanizar(caminho);
}

export function humanizar(chave) {
  const texto = String(chave || "").replace(/_/g, " ").trim();
  return texto ? texto[0].toUpperCase() + texto.slice(1) : "";
}

export default function GerarDocumentoDialog({ modelo, aberto, onFechar, onGerado }) {
  const [cliente, setCliente] = useState(null);
  const [processo, setProcesso] = useState(null);
  const [valores, setValores] = useState({});
  const [nome, setNome] = useState("");
  const [gerando, setGerando] = useState(false);

  // `|| {}` cria um objeto novo a cada render, o que invalidaria o useMemo
  // abaixo sempre e reiniciaria o formulário a cada digitação.
  const macros = useMemo(() => modelo?.macros || {}, [modelo]);
  const camposLivres = useMemo(() => macros.campos_livres || [], [macros]);
  const automaticos = macros.automaticos || [];

  useEffect(() => {
    if (!aberto) return;
    setCliente(null);
    setProcesso(null);
    setNome("");
    setValores(Object.fromEntries(camposLivres.map((campo) => [campo, ""])));
  }, [aberto, camposLivres]);

  const faltaCliente = macros.usa_cliente && !cliente;
  const faltaProcesso = macros.usa_processo && !processo;

  const gerar = async () => {
    setGerando(true);
    try {
      const { data } = await axios.post(
        `${getApiBase()}/documentos/gerar`,
        {
          modelo_id: modelo.modelo_id,
          cliente_id: cliente?.cliente_id || null,
          processo_id: processo?.processo_id || null,
          valores,
          nome: nome.trim() || null,
        },
        { withCredentials: true },
      );
      toast.success("Documento gerado.");
      onGerado(data);
      onFechar();
    } catch (erro) {
      const detalhe = erro?.response?.data?.detail;
      toast.error(detalhe?.message || detalhe || "Não foi possível gerar o documento.");
    } finally {
      setGerando(false);
    }
  };

  if (!modelo) return null;

  return (
    <Dialog open={aberto} onOpenChange={(estado) => !estado && onFechar()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Cabinet Grotesk" }}>Gerar {modelo.nome}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {macros.usa_cliente && (
            <div>
              <Label htmlFor="documento-cliente" className="text-[#335480]">Cliente</Label>
              <div className="mt-1.5" id="documento-cliente">
                <BuscaCliente selecionado={cliente} onSelect={setCliente} onClear={() => setCliente(null)} />
              </div>
            </div>
          )}

          {macros.usa_processo && (
            <div>
              <Label htmlFor="documento-processo" className="text-[#335480]">Processo</Label>
              <div className="mt-1.5" id="documento-processo">
                <BuscaProcesso selecionado={processo} onSelect={setProcesso} onClear={() => setProcesso(null)} />
              </div>
            </div>
          )}

          {/* Mostrar o que o TUTO já responde evita a dúvida "por que este
              campo não está no formulário?" e o retrabalho de digitar de novo. */}
          {automaticos.length > 0 && (
            <div className="border border-[#D1DEEE] rounded-lg bg-[#F4F7FB] p-4">
              <p className="text-[#001533] font-medium mb-2">Preenchido pelo TUTO</p>
              <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5" data-testid="macros-automaticas">
                {automaticos.map((caminho) => (
                  <li key={caminho} className="flex items-start gap-2 text-[#335480]">
                    <Check size={16} strokeWidth={1.5} className="text-[#003882] mt-1 flex-shrink-0" aria-hidden="true" />
                    <span>{rotuloDaMacro(caminho)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {camposLivres.length > 0 && (
            <div className="space-y-4">
              <p className="text-[#001533] font-medium">Campos do modelo</p>
              {camposLivres.map((campo) => (
                <div key={campo}>
                  <Label htmlFor={`campo-${campo}`} className="text-[#335480]">{humanizar(campo)}</Label>
                  <Input
                    id={`campo-${campo}`}
                    data-testid={`campo-${campo}`}
                    value={valores[campo] ?? ""}
                    onChange={(evento) => setValores((atual) => ({ ...atual, [campo]: evento.target.value }))}
                    className="mt-1.5"
                  />
                </div>
              ))}
            </div>
          )}

          <div>
            <Label htmlFor="documento-nome" className="text-[#335480]">Nome do documento</Label>
            <Input
              id="documento-nome"
              data-testid="documento-nome"
              value={nome}
              onChange={(evento) => setNome(evento.target.value)}
              placeholder={cliente ? `${modelo.nome} — ${cliente.nome}` : modelo.nome}
              className="mt-1.5"
            />
            <p className="text-[#335480] text-sm mt-1.5">Em branco, usamos o nome do modelo e o do cliente.</p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onFechar} className="border-[#D1DEEE] text-[#335480]">
            Cancelar
          </Button>
          <Button
            data-testid="confirmar-geracao"
            onClick={gerar}
            disabled={gerando || faltaCliente || faltaProcesso}
            className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
          >
            {gerando ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : null}
            {gerando ? "Gerando..." : "Gerar documento"}
          </Button>
        </div>
        {(faltaCliente || faltaProcesso) && (
          <p className="text-[#335480] text-sm text-right" data-testid="aviso-selecao">
            Escolha {faltaCliente ? "o cliente" : ""}{faltaCliente && faltaProcesso ? " e " : ""}
            {faltaProcesso ? "o processo" : ""} que este modelo usa.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
